import { Link } from 'react-router-dom';
import LegalLayout, { Section } from './LegalLayout';

export default function TermsPage() {
  return (
    <LegalLayout
      title="Terms of Service"
      subtitle="The rules and conditions that govern your use of Gromly."
      lastUpdated="Last updated: October 4, 2026 · Effective date: October 4, 2026"
      relatedLink={{ to: '/privacy', label: 'Privacy Policy →' }}
    >
      <Section number="0" title="Overview">
        <p>
          These Terms of Service ("Terms") govern your use of Gromly, a collaborative trip planning web
          application. By creating an account or using the service, you agree to these Terms. If you do
          not agree, do not use Gromly.
        </p>
        <p>
          The service is operated by <strong>VESTIGI PLATFORM, S.L.</strong> (NIF B26661439), Passatge
          Beat F X Ponsa, 11, 08180 Moià (Barcelona), Spain.
          Contact:{' '}
          <a href="mailto:contact@gromly.io" className="text-teal-600 dark:text-teal-400 hover:underline">
            contact@gromly.io
          </a>.
        </p>
      </Section>

      <Section number="1" title="The Service">
        <p>
          Gromly is a web application that allows users to plan trips collaboratively, including building
          itineraries, splitting expenses, managing bookings, and using AI-powered planning assistance.
        </p>
      </Section>

      <Section number="2" title="Eligibility">
        <ul className="list-disc list-inside space-y-2">
          <li>You must be at least 16 years old to use Gromly.</li>
          <li>You must provide accurate and complete registration information.</li>
          <li>One person may not maintain more than one free account.</li>
          <li>You are responsible for maintaining the confidentiality of your account credentials and for all activity that occurs under your account.</li>
        </ul>
      </Section>

      <Section number="3" title="Credits and Paid Features">
        <p>Gromly operates a credit-based system for certain features:</p>
        <ul className="list-disc list-inside space-y-2">
          <li><strong>Trip Credits:</strong> Each new account receives 1 free trip credit. Additional credits can be purchased at €1.99 per credit. One credit is required to create a new trip.</li>
          <li><strong>AI Credits:</strong> New accounts receive 50 free AI requests. Additional packs of 50 AI requests can be purchased for €0.99.</li>
          <li><strong>Premium AI Credits:</strong> Premium AI credits (using our most capable AI models) can be purchased separately.</li>
        </ul>
        <p>
          All prices are shown inclusive of applicable VAT. Payments are processed by Stripe. By
          purchasing credits you agree to Stripe's{' '}
          <a href="https://stripe.com/legal/consumer" target="_blank" rel="noopener noreferrer" className="text-teal-600 dark:text-teal-400 hover:underline">
            Terms of Service
          </a>.
        </p>
      </Section>

      <Section number="4" title="Refund Policy">
        <p>
          Due to the digital nature of the service, <strong>purchased credits are generally non-refundable</strong>{' '}
          once consumed. However, if you have purchased credits that have not been used and you have a
          legitimate reason for a refund, please contact us at{' '}
          <a href="mailto:contact@gromly.io" className="text-teal-600 dark:text-teal-400 hover:underline">
            contact@gromly.io
          </a>{' '}
          within 14 days of purchase and we will assess your request individually.
        </p>
        <p>
          Pursuant to EU consumer rights regulations (Directive 2011/83/EU), you have the right to
          withdraw from a digital content purchase within 14 days, unless you have expressly consented
          to immediate delivery and acknowledged that the right of withdrawal is lost. By purchasing and
          using credits you acknowledge this.
        </p>
      </Section>

      <Section number="5" title="Acceptable Use">
        <p>You agree not to use Gromly to:</p>
        <ul className="list-disc list-inside space-y-1">
          <li>Upload or share illegal, harmful, or offensive content.</li>
          <li>Impersonate another person or entity.</li>
          <li>Attempt to gain unauthorised access to other users' accounts or data.</li>
          <li>Use automated means to scrape, crawl, or extract data from the service.</li>
          <li>Violate any applicable law or regulation.</li>
          <li>Interfere with or disrupt the service's infrastructure.</li>
        </ul>
        <p>
          We reserve the right to suspend or terminate accounts that violate these rules, without prior
          notice and without refund of unused credits.
        </p>
      </Section>

      <Section number="6" title="Community Features and Published Trips">
        <p>Gromly allows you to publish trips to a public community feed. By publishing a trip you:</p>
        <ul className="list-disc list-inside space-y-2">
          <li>Grant Gromly a non-exclusive licence to display the trip content to other users.</li>
          <li>Allow other users to "clone" your trip as a starting point for their own planning. Cloned trips are independent copies — changes to the clone do not affect your original.</li>
          <li>Confirm that the content you publish does not violate third-party rights or applicable law.</li>
        </ul>
        <p>
          You can unpublish a trip at any time. Unpublishing removes it from the community feed but
          existing clones made by others will remain as independent trips.
        </p>
      </Section>

      <Section number="7" title="AI Features">
        <p>Gromly provides AI-powered trip planning assistance via OpenAI's API. Please note:</p>
        <ul className="list-disc list-inside space-y-2">
          <li>AI suggestions are provided for convenience only and may contain errors or outdated information. Always verify important travel details (visa requirements, opening times, prices, health and safety) from official sources.</li>
          <li>
            Trip content you submit to the AI assistant is sent to OpenAI's API for processing. See our{' '}
            <Link to="/privacy" className="text-teal-600 dark:text-teal-400 hover:underline">Privacy Policy</Link>{' '}
            for details.
          </li>
          <li>We are not liable for decisions made based on AI-generated suggestions.</li>
        </ul>
      </Section>

      <Section number="8" title="Intellectual Property">
        <p>
          The Gromly name, logo, and application code are the property of VESTIGI PLATFORM, S.L. You may not
          reproduce, distribute, or create derivative works from them without prior written permission.
        </p>
        <p>
          You retain ownership of all trip content you create. You grant Gromly a limited licence to
          store, process, and display that content solely to provide the service to you.
        </p>
      </Section>

      <Section number="9" title="Data and Privacy">
        <p>
          Our collection and use of your personal data is governed by our{' '}
          <Link to="/privacy" className="text-teal-600 dark:text-teal-400 hover:underline">Privacy Policy</Link>,
          which is incorporated into these Terms by reference. By using Gromly, you agree to the Privacy Policy.
        </p>
      </Section>

      <Section number="10" title="Service Availability and Changes">
        <p>
          We aim to keep Gromly available 24/7 but do not guarantee uninterrupted access. We may
          modify, suspend, or discontinue the service (or any part of it) at any time. In the event
          of a significant service discontinuation, we will provide reasonable advance notice.
        </p>
        <p>
          We reserve the right to modify these Terms at any time. Material changes will be communicated
          via email or in-app notice at least 14 days before taking effect. Continued use of the
          service after changes constitute your acceptance.
        </p>
      </Section>

      <Section number="11" title="Disclaimer of Warranties">
        <p>
          The service is provided <strong>"as is"</strong> and <strong>"as available"</strong> without
          warranties of any kind, express or implied, including but not limited to merchantability,
          fitness for a particular purpose, or non-infringement. We do not warrant that the service
          will be error-free or that any defects will be corrected.
        </p>
      </Section>

      <Section number="12" title="Limitation of Liability">
        <p>
          To the maximum extent permitted by applicable law, VESTIGI PLATFORM, S.L. shall not be liable for any
          indirect, incidental, special, consequential, or punitive damages arising out of or related
          to your use of Gromly. Our total liability shall not exceed the amount you paid us in the
          12 months preceding the claim.
        </p>
        <p>
          Nothing in these Terms limits our liability for fraud, gross negligence, or death or
          personal injury caused by our negligence, as required by Spanish law.
        </p>
      </Section>

      <Section number="13" title="Governing Law and Jurisdiction">
        <p>
          These Terms are governed by the laws of Spain. Any disputes arising from these Terms or
          your use of the service shall be subject to the exclusive jurisdiction of the courts of
          Spain, without prejudice to any mandatory consumer protection rights you may have under
          the laws of your country of residence within the EU.
        </p>
      </Section>

      <Section number="14" title="Contact">
        <p>
          For any questions about these Terms, please contact:{' '}
          <a href="mailto:contact@gromly.io" className="text-teal-600 dark:text-teal-400 hover:underline">
            contact@gromly.io
          </a>
        </p>
      </Section>
    </LegalLayout>
  );
}
